import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import { getArchiveEmergencyByWorkspaceID, setEmergencyUnarchive } from "../../Api/Services/EmergencyServices.js";
import { getArchivePetNamesByWorkspaceID } from "../../Api/Services/PetServices.js";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import useAuth from "../../ContextProvider/AuthProvider/AuthProvider.js";
import { filterAscDescData, formatPhoneNumber, hexToRgba } from "../../Utils.js";
import DetailsModal from "../Common/DetailsModal.js";
import PawItemList from "../Common/PawItemList.js";
import { useTheme } from "@mui/material/styles";

import "./ArchiveGlobalItemList.css";
import { Button } from "@mui/material";

export default function ArchiveEmergencyContact() {
  const { workspaceId: w_id } = useAuth();
  const [selectedPetName, setSelectedPetName] = useState("");
  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archiveEmergecyContactId, setArchiveEmergecyContactId] = useState(null);
  const [filter, setFilter] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [emergencyData, setEmergencyData] = useState([]);
  const fetchIdRef = useRef(0);

  const navigate = useNavigate();
  const theme = useTheme();

  const { data: data, isLoading } = useSWR(
    w_id ? `archiveEmergencydata${w_id}` : null,
    async () => {
      const { data } = await getArchiveEmergencyByWorkspaceID(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          ...item,
          id: item?.emergency_contact_id,
          phone_number: formatPhoneNumber(item?.phone_number),
        };
      });
      return filteredData || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
  }, []);

  useEffect(() => {
    const { limit, from, sort, ...props } = filter;
    if (filter && filter.from > -1) {
      if (data?.length && limit) {
        setPageCount(Math.ceil((data?.length) / limit));
        const allData = [...data];
        const filteredResult = filterAscDescData(allData, sort, from, limit);
        setEmergencyData(filteredResult);
      }
    }
  }, [filter, data]);


  const { data: petNames = [] } = useSWR(
    w_id ? `petNames${w_id}` : null,
    async () => {
      const { data } = await getArchivePetNamesByWorkspaceID(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  let filteredData;

  if (selectedPetName === "") {
    filteredData = emergencyData;
  } else if (!emergencyData) {
    filteredData = [];
  } else {
    filteredData = emergencyData?.filter((item) => {
      const petName = item.name || "";
      const query =
        selectedPetName && selectedPetName.length > 0
          ? selectedPetName[0].toLowerCase()
          : "";
      return petName.toLowerCase().includes(query);
    });
  }

  const petNamesSet = new Set();
  const filteredPetNames = petNames?.filter((pet) => {
    const lowercaseName = pet.name.toLowerCase();
    if (!petNamesSet.has(lowercaseName)) {
      petNamesSet.add(lowercaseName);
      return true;
    }
    return false;
  });

  const handleArchiveClick = (emergency_contact_id) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchiveEmergecyContactId(emergency_contact_id); // Store the petId in a state variable
  };
  const handleArchive = () => {
    setIsUpdating(true);

    setEmergencyUnarchive(archiveEmergecyContactId) // Use the stored petId from state
      .then((response) => {
        setIsUpdating(false);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };

  const defaultOptions = {
    pageSize: 5,
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "image",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.image ? (
            <img
              src={row?.original?.image}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
              alt=''
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Relationship`,
        accessor: "relationship",
        width: "12.5vw",
      },
      {
        Header: "Phone Number",
        accessor: "phone_number",
        width: "12.5vw",
      },
      {
        Header: "",
        accessor: "emergency_contact_id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() =>
                handleArchiveClick(row?.original?.emergency_contact_id)
              }
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        disableSortBy: true,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <Button
            sx={{
              minWidth: "40px",
              background: `${hexToRgba(theme.palette.primary.main, 0.20)} !important`,
              borderRadius: "10px",
              marginRight: "5px",
              width: "24px",
            }}
            onClick={() => navigate(props.viewDataLink(row.original))}
          >
            <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
          </Button>
          );
        },
      },
    ];
  }, []);

  const props = {
    label: "Archive Emergency Contacts",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    isLoading,
    dataAddLink: "/Archive",
    columns,
    columnsData: filteredData,
    viewDataLink: (data) =>
      `/archive/emergency-info/${data.emergency_contact_id}`,
    placeholder: "pet name",
    details: "There are no emercency contact details for this user",
    isArchive: true,
    AddPetArchive: true,
    fetchData,
    pageCount,
    defaultOptions,
  };

  const ButtonItem = [
    {
      name: "yes",
      onClick: () => {
        handleArchive();
        setShowSuccessMessage(false);
      },
    },
    {
      name: "no",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  return (<>
    <PawItemList {...props} />
  {showSuccessMessage && (
    <>
      <DetailsModal
        open={open}
        severity="warning"
        buttonList={ButtonItem}
          title="Do you want to unarchive this emergecy contact?"
          handleClose={()=> setOpen(false)}
      />
    </>
    )}
    </>)
}
